import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import layoutActions from '@/modules/layout/layoutActions';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useMatch, useSearchParams } from 'react-router-dom';
import layoutSelectors from 'src/modules/layout/layoutSelectors';
import { getHistory } from 'src/modules/store';
import Header from 'src/view/layout/Header';
import Menu from 'src/view/layout/Menu';

interface LayoutProps {
  children: React.ReactNode;
  menus: any[];
  match?: any;
}

const Layout = ({ children, menus }: LayoutProps) => {
  const location = useLocation();
  const routeMatch = useMatch(location.pathname);
  const [searchParams] = useSearchParams();
  const pdf = searchParams.get('pdf');
  const [_showDrawer, setShowDrawer] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dispatch = useDispatch();

  const match = {
    path: location.pathname,
  };

  const menuVisible = useSelector(
    layoutSelectors.selectMenuVisible,
  );

  // Toggle menu visibility
  const toggleMenu = () => {
    dispatch(layoutActions.doToggleMenu());
  };

  useEffect(() => {
    // Check if device is mobile
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    // Initial check
    checkIfMobile();

    // Auto-collapse menu on desktop and hide on mobile
    if (window.innerWidth <= 1600) {
      dispatch(layoutActions.doToggleMenu());
    }

    if (window.innerWidth <= 640) {
      dispatch(layoutActions.doHideMenu());
    }

    // Add resize listener
    window.addEventListener('resize', checkIfMobile);

    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, [dispatch]);

  // Detect if we should show the drawer view
  const showView = Boolean(
    location.pathname.includes('/new') ||
      Boolean(routeMatch?.params?.id) ||
      location.pathname.includes('/view'),
  );

  // Handle drawer visibility
  useEffect(() => {
    setShowDrawer(showView);
  }, [showView]);

  // PDF capture mode: omit Header + Menu chrome so the printed document
  // shows only the page body. Suppression mirrors the gates in App.tsx
  // (Toaster) and ResourceViewPage.tsx (sidebar).
  if (pdf) {
    return (
      <div className="w-full max-w-full">{children}</div>
    );
  }

  // Handle builder mode
  if (match?.path?.includes?.('builder')) {
    return (
      <div className="flex flex-col h-screen max-w-full">
        <Header showCollapse={false} />
        <Separator />
        <div className="flex-1 min-w-0">
          <main className="h-full w-full">
            <div className="w-full max-w-full">
              {children}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Handle drawer close and navigation
  const _handleClose = () => {
    const pathMap = {
      '/location/': '/location',
      '/resource/': '/resource',
      '/keyword/': '/keyword',
      'form-category': '/form-category',
      '/asset/': '/asset',
      '/notification/': '/notification',
      '/form-template': '/form-template',
      '/workflow/': '/workflow',
      '/flow-step/': '/flow-step',
      '/data-studio/': '/data-studio',
      '/app-user/': '/app-user',
      '/user/': '/user',
      '/tenant/': '/tenant',
      '/api-key/': '/api-key',
      '/indicator/': '/indicator',
    };

    for (const [path, redirect] of Object.entries(
      pathMap,
    )) {
      if (match?.path?.includes?.(path)) {
        getHistory().push(redirect);
        break;
      }
    }
  };

  return (
    <div className="h-screen flex flex-col md:flex-row overflow-x-hidden bg-background dark:bg-gradient-to-b dark:from-background dark:to-background/95">
      {/* Backdrop overlay (mobile only) - only visible when menu is open */}
      {isMobile && menuVisible && (
        <div
          className="fixed inset-0 bg-black/50 z-10"
          onClick={toggleMenu}
          aria-hidden="true"
        />
      )}

      {/* Menu */}
      <div
        className={cn(
          // Base position and styles
          'h-screen z-20 shrink-0',
          // Dynamic width based on menuVisible
          menuVisible ? 'w-64' : 'w-16',
          // Mobile specific styles
          isMobile && 'fixed left-0 top-0 bottom-0',
          // Show/hide based on menuVisible (only affects mobile)
          isMobile && !menuVisible && '-translate-x-full',
          // Animation
          'transition-all duration-300 ease-in-out',
          // Style
          'bg-background border-r',
        )}
      >
        {/* Mobile menu close button */}
        {isMobile && menuVisible && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4"
            onClick={toggleMenu}
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
        <Menu url={location.pathname} menus={menus} />
      </div>

      {/* Main Content Container */}
      <div className="flex-1 flex flex-col h-screen min-w-0">
        {/* Header */}
        <Header />
        <Separator />

        {/* Content Area */}
        <main
          className={cn(
            'flex-1 overflow-y-auto w-full',
            location.pathname.includes('dashboard-studio')
              ? 'p-0'
              : 'p-2 sm:p-0 md:p-0 lg:p-0 space-y-0 sm:space-y-0 md:space-y-0',
            'bg-background/50 backdrop-blur-[1px]',
          )}
        >
          <div className="w-full max-w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
